import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Anchor } from "@impero-com/ui";

import { translateMonth } from "org/control/list/utils";

import { useFeatureFlag } from "../../../../utils/hooks/useFeatureFlag";

import {
    TestingControlBulkCreateSchema,
    TestingControlFormSchema,
} from "./testingControlSchema";
import { TestingControlPageType } from "./types";

export const TESTING_CONTROL_DETAILS_ANCHOR = "testing-control-details";
export const TESTING_CONTROL_SCOPE_ANCHOR = "testing-control-scope";
export const TESTING_CONTROL_BULK_ANCHOR = "testing-control-bulk";
export const TESTING_CONTROL_ASSIGN_ANCHOR = "testing-control-assign";
export const TESTING_CONTROL_SCHEDULE_ANCHOR = "testing-control-schedule";
export const TESTING_CONTROL_PLANNING_ANCHOR = "testing-control-planning";
export const TESTING_CONTROL_REMINDERS_ANCHOR = "testing-control-reminders";

// The order MATTERS
export const TESTING_CONTROL_ANCHORS = [
    TESTING_CONTROL_DETAILS_ANCHOR,
    TESTING_CONTROL_SCHEDULE_ANCHOR,
    TESTING_CONTROL_PLANNING_ANCHOR,
    TESTING_CONTROL_SCOPE_ANCHOR,
    TESTING_CONTROL_ASSIGN_ANCHOR,
    TESTING_CONTROL_REMINDERS_ANCHOR,
] as const;

export const TESTING_CONTROL_BULK_ANCHORS = [
    TESTING_CONTROL_SCOPE_ANCHOR,
    TESTING_CONTROL_SCHEDULE_ANCHOR,
    TESTING_CONTROL_BULK_ANCHOR,
    TESTING_CONTROL_ASSIGN_ANCHOR,
    TESTING_CONTROL_PLANNING_ANCHOR,
    TESTING_CONTROL_REMINDERS_ANCHOR,
] as const;

export type TestingControlAnchor =
    | (typeof TESTING_CONTROL_ANCHORS)[number]
    | (typeof TESTING_CONTROL_BULK_ANCHORS)[number];

export function useTestingControlViewAnchorLabels(
    isSingleMode = true
): Record<TestingControlAnchor, string> {
    const { t } = useTranslation();
    return {
        [TESTING_CONTROL_DETAILS_ANCHOR]: t("TestingControlView_Details_Title"),
        [TESTING_CONTROL_SCOPE_ANCHOR]: isSingleMode
            ? t("TestingControlView_Setup_Title")
            : t("TestingControlView_Setup_Title_Bulk"),
        [TESTING_CONTROL_ASSIGN_ANCHOR]: t("TestingControlView_Assign_Title"),
        [TESTING_CONTROL_BULK_ANCHOR]: t("TestingControlView_Bulk_Title"),
        [TESTING_CONTROL_SCHEDULE_ANCHOR]: t(
            "TestingControlView_Schedule_Title"
        ),
        [TESTING_CONTROL_PLANNING_ANCHOR]: t(
            "TestingControlView_Planning_Title"
        ),
        [TESTING_CONTROL_REMINDERS_ANCHOR]: t(
            "TestingControlView_Reminders_Title"
        ),
    };
}

export function useTestingControlViewAnchors(
    pageType: TestingControlPageType
): Anchor[] {
    const showControlTestingExperimental = useFeatureFlag(
        "controlTestingExperimental"
    );
    const getPageTypeAnchors = () => {
        switch (pageType) {
            case "BULK_CREATE":
                return TESTING_CONTROL_BULK_ANCHORS.filter(
                    (anchor) =>
                        showControlTestingExperimental ||
                        anchor !== TESTING_CONTROL_REMINDERS_ANCHOR
                );
            case "CREATE":
            case "EDIT":
                return TESTING_CONTROL_ANCHORS.filter(
                    (anchor) =>
                        showControlTestingExperimental ||
                        anchor !== TESTING_CONTROL_REMINDERS_ANCHOR
                );
            default:
                return assertNever(pageType);
        }
    };

    const anchorLabels = useTestingControlViewAnchorLabels();

    return getPageTypeAnchors().map((anchor) => ({
        elementId: anchor,
        label: anchorLabels[anchor],
    }));
}

export const orderedMonthOptions = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
] as const;

export const getMonthSelectOptions = () =>
    orderedMonthOptions.map((month) => ({
        value: month,
        label: translateMonth(month),
    }));

export const MIN_YEAR = 1900;
export const MAX_YEAR = new Date().getFullYear();

export const yearOptions = Array.from(
    { length: MAX_YEAR - MIN_YEAR + 1 },
    (_, i) => MIN_YEAR + i
)
    .map((year) => ({
        value: year,
        label: year.toString(),
    }))
    .toReversed();

const todayNoon = new Date(new Date().setHours(12, 0));

// Recursive type utility to make all properties optional and allow undefined values, including nested objects
type OptionalWithUndefined<T> = {
    [K in keyof T]?: T[K] extends object
        ? OptionalWithUndefined<T[K]>
        : T[K] | undefined;
};
export function getTestingControlFormDefaultValues(
    timezoneName: TestingControlFormSchema["timezoneName"],
    pageType: TestingControlPageType
): TestingControlFormSchema {
    const commonValues: OptionalWithUndefined<TestingControlFormSchema> = {
        tagIds: [],
        title: "",
        description: "",
        samplingTagIds: [],
        notificationTime: todayNoon,
        responsible: {
            userId: undefined,
            allocatedTime: {
                unit: "Days" as const,
                value: 1,
            },
        },
        reviewers: {
            reviewer: {
                userId: undefined,
                allocatedTime: {
                    unit: "Days" as const,
                    value: 1,
                },
            },
            secondReviewer: {
                userId: undefined,
                allocatedTime: {
                    unit: "Days" as const,
                    value: 1,
                },
            },
        },
        period: {
            start: undefined,
            end: undefined,
        },
        dueDate: todayNoon,
        timezoneName,
        randomSampleCount: 0,
    };

    switch (pageType) {
        case "EDIT":
            return {
                ...commonValues,
                controlId: undefined,
            } as TestingControlFormSchema;
        case "CREATE":
            return {
                ...commonValues,
                controlId: undefined,
            } as TestingControlFormSchema;
        case "BULK_CREATE":
            return {
                ...commonValues,
                selectedControlIds: [],
            } as unknown as TestingControlBulkCreateSchema;
        default:
            return assertNever(pageType);
    }
}
